import CloseIcon from '@mui/icons-material/Close'
import { Box, Typography } from '@mui/material'
import MuiDialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import type { SxProps, Theme } from '@mui/material/styles'
import { useTheme } from '@mui/material/styles'
import * as React from 'react'

import { IconButton } from './IconButton'

interface DialogProps {
  triggerElement: React.ReactNode
  actionButtons?: React.ReactNode
  title: React.ReactNode
  content: React.ReactNode | null
  isOpen: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  paperStyles?: SxProps<Theme>
  onClose?: () => void
  showCloseButton?: boolean
  preventBackdropClose?: boolean
  progressBar?: React.ReactNode
}

const Dialog: React.FC<DialogProps> = ({
  triggerElement,
  actionButtons,
  title,
  content,
  isOpen,
  maxWidth = 'sm',
  paperStyles,
  onClose,
  showCloseButton = true,
  preventBackdropClose = false,
  progressBar,
}) => {
  const theme = useTheme()

  const handleDialogClose = React.useCallback(() => {
    onClose?.()
  }, [onClose])

  const handleBackdropClose = React.useCallback(
    (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (
        preventBackdropClose &&
        (reason === 'backdropClick' || reason === 'escapeKeyDown')
      ) {
        return
      }
      handleDialogClose()
    },
    [preventBackdropClose, handleDialogClose],
  )

  return (
    <React.Fragment>
      {triggerElement}
      <MuiDialog
        open={isOpen}
        onClose={handleBackdropClose}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-content"
        maxWidth={maxWidth}
        slotProps={{
          paper: {
            sx: {
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              marginTop: '5.625rem',
              height: 'auto',
              p: 0,
              maxHeight: 'calc(100% - 130px)',
              color: theme.palette.text.primary,
              ...paperStyles,
              '& .MuiDialogContent-root': {
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.background.default,
                },
              },
            },
          },
        }}
      >
        <DialogTitle sx={{ py: 2, px: 3 }} id="dialog-title">
          <Box
            display="flex"
            justifyContent={showCloseButton ? 'space-between' : 'center'}
            alignItems="center"
          >
            <Typography
              fontWeight={600}
              fontSize={'1.25rem'}
              lineHeight={'2rem'}
            >
              {title}
            </Typography>
            {showCloseButton && (
              <IconButton
                onClick={handleDialogClose}
                aria-label="close dialog"
                sx={{
                  padding: 0,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CloseIcon sx={{ color: theme.palette.text.primary }} />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        {progressBar && <Box sx={{ width: '100%' }}>{progressBar}</Box>}
        {content && (
          <DialogContent
            sx={{ py: 2.5, px: 3, paddingTop: '20px !important' }}
            id="dialog-content"
          >
            {content}
          </DialogContent>
        )}
        {actionButtons && (
          <DialogActions
            sx={{
              py: 2,
              px: 3,
              bgcolor: 'transparent',
            }}
          >
            <Box display="flex" justifyContent="end" gap={2} flexGrow={1}>
              {actionButtons}
            </Box>
          </DialogActions>
        )}
      </MuiDialog>
    </React.Fragment>
  )
}

export default Dialog
